import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { lastValueFrom } from 'rxjs';
import { Assignment, AssignmentWithRelations } from '../../../../api/app-types/assignment';
import { ReportDocument } from '../../../../api/app-types/report-document.app';
import { User } from '../../../../api/app-types/user.app';
import { LvrHeading } from '../../../../core/components/heading/heading';
import { GenericDialog } from '../../../../core/components/generic-dialog/generic-dialog';
import { CarouselElement, NavCarousel } from '../../../../core/components/nav-carousel/nav-carousel';
import { AccessService } from '../../../../core/services/access.service';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { ClientResolved } from '../../../../shared/routing/resolvers/client.resolver';
import { RouteInjection } from '../../../../shared/routing/route-injection.class';
import { SERVICE_OFFICE_STATEMENT_API } from '../../../../api/services/service-office-statement.service';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../shared/notification/notification.factory';
import { LvrDocumentItem } from '../../../../core/components/document-item/document-item';
import {
	DOCUMENT_CATEGORY,
	DocumentEntry,
	DocumentFile,
	DocumentNavItem,
} from '../../../../api/app-types/document.app';
import { DB_PHASES } from '../../../../api/app-types/phase.app';
import { LvrButton } from '../../../../core/components/lvr-button/lvr-button';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { downloadDocumentFiles } from '../../../../shared/helpers/document-download.func';
import { MatChip } from '@angular/material/chips';
import { CLARIFICATION_TYPE, getClarificationTypeOptions } from '../../../../api/app-types/relative.app';
import { CUSTOM_DATE_FORMATS } from '../../../../core/adapters/custom-date-format';
import { MatTooltipModule } from '@angular/material/tooltip';
import { REPRESENTATION_MODE } from '../../../../core/services/representation-mode.service';
import { RECORD_MANAGER } from '../../../../core/services/record.provide';
import { Client, RestrictedClient } from '../../../../api/app-types/client.app';
import { STATE } from '../../../../core/services/state.service';
import { canAccessFdsReport, hasQualifyingCaseAccess } from '../../../../shared/permissions/fds-report-access.func';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';

@Component({
	selector: 'lvr-fds-assignment-details',
	templateUrl: './fds-assignment-details.html',
	styleUrl: './fds-assignment-details.scss',
	imports: [
		DatePipe,
		NavCarousel,
		LvrHeading,
		TranslocoPipe,
		MatDialogModule,
		LvrDocumentItem,
		LvrButton,
		MatChip,
		MatTooltipModule,
		LvrPaddingDirective,
	],
})
export class FdsAssignmentDetails extends RouteInjection {
	public readonly isSubmitView = input<boolean>(false);
	private readonly access = inject(AccessService);
	private readonly dialog = inject(MatDialog);
	private readonly serviceOfficeStatementApi = inject(SERVICE_OFFICE_STATEMENT_API);
	private readonly notificationService = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly session = inject(SessionManagerService);
	private readonly state = inject(STATE);
	private representationMode = inject(REPRESENTATION_MODE);
	private readonly recordManager = inject(RECORD_MANAGER);
	public readonly isRepresentationModeActive = this.representationMode.isActive;
	private readonly assignmentsPage = this.resolveDataAsSignal<PaginatedResponse<Assignment> | Assignment[]>(
		'assignments',
	);
	private readonly reportsPage = this.resolveDataAsSignal<PaginatedResponse<ReportDocument>>('reports');
	private readonly user = this.resolveDataAsSignal<User>('user');
	private readonly client = this.resolveDataAsSignal<ClientResolved>('client');
	public readonly assignment = this.resolveDataAsSignal<AssignmentWithRelations>('assignment');
	private readonly lastTitle = signal<string | null>(null);
	public readonly currentDatum = computed(() => new Date());
	public readonly canEditAssignmentPermission = hasPermission(this.session.permissions, 'Assignments.Edit');

	public readonly canEditAssignment = computed(() => {
		const assignment = this.assignment();
		const client = this.client();

		if (!assignment) return false;
		if (client?.kind === 'restricted') return false;
		if (!this.canEditAssignmentPermission()) return false;

		return (
			(this.isFdsAssignmentOwner(assignment) && !this.isRepresentationModeActive()) ||
			(this.isRepresentationModeActive() && this.isRepresentingFdsAssignmentOwner(assignment))
		);
	});

	private readonly recordTitle = computed(() => {
		const c = this.client();
		const v = c?.value as Client | RestrictedClient;
		const name = v?.name?.trim() ?? '';
		const firstName = v?.firstName?.trim() ?? '';
		if (name && firstName) return `${name}, ${firstName}`;
		if (name) return name;
		if (firstName) return firstName;
		return 'Klient';
	});

	public constructor() {
		super();
		effect(() => {
			const title = this.recordTitle();
			if (this.lastTitle() === title) return;
			// To avoid effect loop
			untracked(() => this.recordManager.updateRecordTitleOfActiveRecord(title));
			this.lastTitle.set(title);
		});
	}

	private readonly hasSystemAdminPermission = hasPermission(this.session.permissions, 'System.Admin');
	private readonly canResetAssignments = hasPermission(this.session.permissions, 'Assignments.Reset');
	public readonly canReadAssignmentDocuments = hasPermission(this.session.permissions, 'Documents.Assignment.Read');
	public readonly canEditReportsPermission = hasPermission(this.session.permissions, 'Reports.Edit');
	public readonly canReadReportsPermission = hasPermission(this.session.permissions, 'Reports.Read');
	public readonly isAdmin = computed(() => {
		const role = this.user()?.roles?.shortName?.toLowerCase();
		return this.hasSystemAdminPermission() || role === 'admin';
	});

	public readonly fp5Id = DB_PHASES.FP5;
	public readonly shouldShowFp5CancellationDateTitle = computed(() => {
		const assignment = this.assignment();
		return assignment?.phaseId === DB_PHASES.FP5 || assignment?.phaseChangeId === DB_PHASES.FP5;
	});

	public readonly canSOSReset = computed(
		() => this.assignment()?.phaseId === DB_PHASES.FP4 && this.isAdmin() && this.canResetAssignments(),
	);

	public readonly assignments = computed(() =>
		this.getResolvedAssignments().filter((item) => item.isServiceOfficeStatement),
	);

	public readonly carouselElements = computed(() =>
		this.assignments().map((assignment) => ({
			id: assignment.id.toString(),
			label: assignment.phase?.remark ?? assignment.id.toString(),
		})),
	);
	public readonly currentIndex = computed(() => {
		const assignmentId = this.assignment()?.id?.toString();
		return this.carouselElements().findIndex((element) => element.id === assignmentId);
	});
	public readonly backLink = computed(() =>
		this.router.createUrlTree(['../../'], { relativeTo: this.route }).toString(),
	);
	public readonly submittedReports = computed(() => (this.reportsPage()?.entities ?? []).filter((report) => report));
	public readonly shouldNotBeDeleted = computed(
		() => this.assignment()?.phaseId === DB_PHASES.FP4 || this.assignment()?.phaseId === DB_PHASES.FP5,
	);
	public readonly dateDisplayFormat = CUSTOM_DATE_FORMATS.display.dateInput;

	public onNavigate(event: { index: number; element: CarouselElement }) {
		this.router.navigate(['../', event.element.id, 'fds'], { relativeTo: this.route });
	}

	public openEdit() {
		if (!this.canEditAssignment()) return;
		this.router.navigate(['../', 'edit-fds'], { relativeTo: this.route });
	}

	public openMail(recipient: string) {
		const assignmentGuid = this.assignment()?.guid ?? '-';
		const url = encodeURI(window.location.href);
		const body = `Auftrag Nummer: ${assignmentGuid}\nLink: ${url}`;
		const href = this.buildMailto(recipient, `KlifdWeb: Auftrag - Nummer: ${assignmentGuid}`, body);
		window.location.href = href;
	}

	public async setReset() {
		const assignment = this.assignment();
		const clientId = assignment?.clientId ?? this.client()?.value.id;
		if (!assignment || !clientId) return;

		const confirmed = await lastValueFrom(
			this.dialog
				.open(GenericDialog, {
					width: '480px',
					data: {
						titleKey: 'assignment.fds.reset_title',
						messageKey: 'assignment.fds.reset_info',
						confirmKey: 'assignment.fds.reset_confirm',
						cancelKey: 'base.button.cancel',
						iconConfirm: '',
					},
				})
				.afterClosed(),
		);
		if (!confirmed) return;

		try {
			await this.serviceOfficeStatementApi.reset(clientId, assignment.id);
			await this.state.updateAssignments();
			this.notificationService.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.assignment_reset')),
			);
			this.router.navigate(['/desk/client', clientId, 'assignments']);
		} catch (error) {
			console.error(error);
			this.notificationService.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.assignment_reset_error')),
			);
		}
	}

	public openTemplateRoute(report?: ReportDocument) {
		if (!this.canOpenReport()) return;

		if (report) {
			if ((report.documents ?? []).length > 0) {
				downloadDocumentFiles(report.documents ?? []);
				return;
			}
			this.router.navigate(['template', report.id], { relativeTo: this.route });
			return;
		}
		this.router.navigate(['template'], { relativeTo: this.route });
	}

	public onFileClick(file: DocumentFile) {
		if (!this.canReadAssignmentDocuments()) return;
		const navItems = this.buildAssignmentNavItems(this.assignment());
		this.router.navigate(['/desk/client', this.client()?.value.id, 'documents', file.id], { state: { navItems } });
	}

	public buildDocumentEntryFromFdsAssignment(doc: DocumentFile): DocumentEntry {
		const assignment = this.assignment() as AssignmentWithRelations;
		const createdAt = assignment.careStartDate ?? new Date();

		return {
			id: assignment.id,
			name: doc.name,
			category: DOCUMENT_CATEGORY.ASSIGNMENT,
			createdAt: createdAt,
			externalFileReference: assignment.documents ?? [],
			isReport: false,
			isFrozen: this.shouldNotBeDeleted(),
		};
	}

	private buildAssignmentNavItems(assignmentWithRelations: AssignmentWithRelations | undefined): DocumentNavItem[] {
		const assignment = assignmentWithRelations;
		if (!assignment) return [];

		const files = assignment.documents ?? [];
		const entryName = assignment.phase.remark || '-';
		const createdAt = assignment.careStartDate ?? new Date();

		return files.map((file) => ({
			file,
			entryId: assignment.id,
			entryName,
			category: DOCUMENT_CATEGORY.ASSIGNMENT,
			createdAt,
			isReport: false,
			isFrozen: this.shouldNotBeDeleted(),
		}));
	}

	public readonly clarificationTypes = getClarificationTypeOptions();

	public getClarificationLabel(id?: CLARIFICATION_TYPE) {
		const type = this.clarificationTypes.find((t) => t.value === id);
		return type ? type.label : '';
	}

	private isFdsAssignmentOwner(item: Assignment): boolean {
		const currentUserId = this.user()?.id;
		if (!currentUserId) return false;

		return String(currentUserId) === String(item.createdByUser?.id);
	}

	private isRepresentingFdsAssignmentOwner(item: Assignment): boolean {
		if (!this.isRepresentationModeActive()) return false;

		const representedUserId = this.representationMode.mode().representedUserId;
		if (representedUserId == null) return false;

		return String(representedUserId) === String(item.createdByUser?.id);
	}

	private getResolvedAssignments(): Assignment[] {
		const assignments = this.assignmentsPage();
		if (!assignments) return [];
		if (Array.isArray(assignments)) return assignments;
		return assignments.entities ?? [];
	}

	private getEffectiveUserId(): string | number | null | undefined {
		if (this.isRepresentationModeActive()) {
			return this.representationMode.mode().representedUserId;
		}
		return this.user()?.id;
	}

	private hasQualifyingCaseAccess(assignment: Assignment): boolean {
		return hasQualifyingCaseAccess(assignment, this.getResolvedAssignments(), this.getEffectiveUserId());
	}

	public canOpenReport(): boolean {
		const assignment = this.assignment();
		const hasOwnerLikeAccess =
			!!assignment &&
			(this.isFdsAssignmentOwner(assignment) ||
				this.isRepresentingFdsAssignmentOwner(assignment) ||
				this.hasQualifyingCaseAccess(assignment));

		return canAccessFdsReport({
			assignment,
			relatedAssignments: this.getResolvedAssignments(),
			effectiveUserId: this.getEffectiveUserId(),
			hasReadPermission: this.canReadReportsPermission(),
			hasEditPermission: this.canEditReportsPermission(),
			hasOwnerLikeAccess,
			roleId: this.user()?.roles?.id,
		});
	}

	/**
	 * Builds a safe mailto U with properly encoded subject and body.
	 * Encodes special characters to prevent URL breakage (spaces, &, ?, etc.)
	 */
	private buildMailto(recipient: string, subject: string, body: string): string {
		const encoded = (s: string) => encodeURIComponent(s ?? '');
		const to = encodeURIComponent(recipient);
		return `mailto:${to}?subject=${encoded(subject)}&body=${encoded(body)}`;
	}
}
